import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconColorPicker, IconDownload, IconEye, IconGripVertical, IconEyeOff, IconMinus, IconPlus, IconRefresh, IconUpload, IconPrinter, IconTrash, IconZoomIn, IconZoomOut, IconZoomReset } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { recordToolUsage } from '../../../../../services/toolsService';
import Hint from '../../../../../components/common/Hint';
import ConfirmDialog from '../../../../../components/common/ConfirmDialog';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolCard from '../../components/common/ToolCard';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { toDisplayableBlob } from '../../image/shared/heicDisplay';
import InvoiceDocument from './InvoiceDocument';
import OutputFormatSelect from '../../image/shared/OutputFormatSelect';
import { PAGE_OUTPUT_FORMATS, encodeCanvas } from '../../image/shared/imageOutputFormats';
import { canvasToPdfBlob, captureInvoiceCanvas } from './exportInvoice';
import {
    ACCENT_PRESETS,
    CLASSIC_COLORS,
    CLASSIC_SHOW,
    PHONE_CHARS,
    SHOP_COLORS,
    MAX_COLUMNS,
    MAX_ROWS,
    MIN_COLUMNS,
    MIN_ROWS,
    PAPER_IDS,
    STORAGE_KEY,
    WATERMARK_MODES,
    TEMPLATES,
    defaultMemo,
    isValidEmail,
    isValidPhone,
    loadDraft,
    newColumn,
    paperSize,
    readLogoFile,
    saveDraft,
} from './invoiceEngine';

/** What this project's layout needs to title the page. */
const PAGE_PROPS = { slug: 'general/invoice-designer', showTrust: false };

const MAX_LOGO_BYTES = 4 * 1024 * 1024;

const inputClass = 'w-full rounded-lg border border-[#ECE8E4] bg-white px-3 py-2 text-[13px] text-[#3D3833] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500';
const labelClass = 'mb-1 block text-[12px] font-semibold text-[#3D3833] dark:text-zinc-200';
const invalidClass = 'border-red-300 focus:border-red-400 dark:border-red-500/50';

function Field({ label, children, className = '', hint, error }) {
    return (
        <label className={`block ${className}`}>
            <span className={labelClass}>{label}</span>
            {children}
            {error ? (
                <span role="alert" className="mt-1 block text-[11.5px] font-medium leading-snug text-red-600 dark:text-red-400">
                    {error}
                </span>
            ) : hint ? (
                <span className="mt-1 block text-[11.5px] leading-snug text-[#A8A099] dark:text-zinc-500">{hint}</span>
            ) : null}
        </label>
    );
}

/** Phone and email side by side, each saying what is wrong when it is. */
function ContactFields({ value, onChange, t }) {
    const phoneBad = !isValidPhone(value.phone);
    const phone2Bad = !isValidPhone(value.phone2);
    const emailBad = !isValidEmail(value.email);
    return (
        <div className="grid grid-cols-1 items-start gap-2.5 sm:grid-cols-2">
            <Field
                label={t('invoiceDesigner.phone')}
                hint={t('invoiceDesigner.phoneHint')}
                error={phoneBad ? t('invoiceDesigner.errors.phone') : undefined}
            >
                <input
                    type="text"
                    inputMode="tel"
                    autoComplete="off"
                    className={`${inputClass} ${phoneBad ? invalidClass : ''}`}
                    aria-invalid={phoneBad}
                    value={value.phone}
                    onChange={(e) => onChange({ phone: e.target.value.replace(PHONE_CHARS, '') })}
                />
            </Field>
            <Field
                label={t('invoiceDesigner.phone2')}
                hint={t('invoiceDesigner.phone2Hint')}
                error={phone2Bad ? t('invoiceDesigner.errors.phone') : undefined}
            >
                <input
                    type="text"
                    inputMode="tel"
                    autoComplete="off"
                    className={`${inputClass} ${phone2Bad ? invalidClass : ''}`}
                    aria-invalid={phone2Bad}
                    value={value.phone2}
                    onChange={(e) => onChange({ phone2: e.target.value.replace(PHONE_CHARS, '') })}
                />
            </Field>
            <Field
                label={t('invoiceDesigner.email')}
                hint={t('invoiceDesigner.emailHint')}
                error={emailBad ? t('invoiceDesigner.errors.email') : undefined}
            >
                <input
                    type="email"
                    inputMode="email"
                    autoComplete="off"
                    className={`${inputClass} ${emailBad ? invalidClass : ''}`}
                    aria-invalid={emailBad}
                    value={value.email}
                    onChange={(e) => onChange({ email: e.target.value })}
                />
            </Field>
        </div>
    );
}

/** − 12 + : how many blank lines the pad is ruled into. */
function RowStepper({ value, onChange, decLabel, incLabel, lang }) {
    const btn = 'flex h-[38px] w-10 flex-none items-center justify-center text-[#6B635C] transition-colors hover:bg-amber-50 hover:text-amber-600 disabled:cursor-not-allowed disabled:opacity-40 dark:text-zinc-300 dark:hover:bg-amber-900/20';
    return (
        <div className="flex h-[38px] items-center overflow-hidden rounded-lg border border-[#ECE8E4] bg-white dark:border-zinc-700 dark:bg-zinc-900">
            <Hint label={decLabel}>
                <button type="button" aria-label={decLabel} disabled={value <= MIN_ROWS} className={btn} onClick={() => onChange(Math.max(MIN_ROWS, value - 1))}>
                    <IconMinus size={15} stroke={2.2} />
                </button>
            </Hint>
            <span className="flex-1 text-center text-[13px] font-semibold text-[#3D3833] dark:text-zinc-100">
                {lang === 'bn' ? toBnDigitsLocal(value) : value}
            </span>
            <Hint label={incLabel}>
                <button type="button" aria-label={incLabel} disabled={value >= MAX_ROWS} className={btn} onClick={() => onChange(Math.min(MAX_ROWS, value + 1))}>
                    <IconPlus size={15} stroke={2.2} />
                </button>
            </Hint>
        </div>
    );
}

function ToggleRow({ label, checked, onChange, className = '' }) {
    return (
        <div className={`flex items-center justify-between gap-3 ${className}`}>
            <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">{label}</span>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                aria-label={label}
                onClick={() => onChange(!checked)}
                className={`relative h-[22px] w-10 flex-none rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-[#D6D6DE] dark:bg-zinc-600'}`}
            >
                <span className={`absolute top-[3px] block h-4 w-4 rounded-full bg-white shadow transition-[left] ${checked ? 'left-[21px]' : 'left-[3px]'}`} />
            </button>
        </div>
    );
}

/** A row of on/off chips — which blanks the form is printed with. */
function ChipToggle({ label, checked, onChange }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                checked
                    ? 'border-amber-500 bg-amber-50 text-amber-700 dark:border-amber-500 dark:bg-amber-900/30 dark:text-amber-300'
                    : 'border-[#ECE8E4] bg-white text-[#A8A099] hover:text-[#3D3833] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-500'
            }`}
        >
            {checked ? <IconEye size={13} stroke={2} /> : <IconEyeOff size={13} stroke={2} />}
            {label}
        </button>
    );
}

/** A labelled slider — the watermark's weight, size and tilt. */
function SliderRow({ label, value, min, max, step = 1, suffix = '', onChange, lang }) {
    const shown = lang === 'bn' ? toBnDigitsLocal(value) : value;
    return (
        <label className="block">
            <span className="mb-1 flex items-center justify-between gap-2">
                <span className={labelClass}>{label}</span>
                <span className="text-[11.5px] font-semibold text-[#A8A099] dark:text-zinc-500">{shown}{suffix}</span>
            </span>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="h-5 w-full accent-amber-500"
            />
        </label>
    );
}

/** A tiny drawing of each memo pad, so the choice is visible before it is made. */
function TemplateThumb({ id, accent, active }) {
    const line = active ? '#C9C2BA' : '#D9D3CC';

    // The outlined pad: every piece of furniture drawn as a rule.
    if (id === 'memoOutline') {
        return (
            <svg viewBox="0 0 60 80" width="46" height="62" aria-hidden="true">
                <rect x="0.5" y="0.5" width="59" height="79" rx="3" fill="#FFFFFF" stroke={line} />
                <rect x="5" y="6" width="50" height="20" rx="2" fill="none" stroke={accent} strokeWidth="1.2" />
                <rect x="22" y="4" width="16" height="4" rx="2" fill="#FFFFFF" stroke={accent} strokeWidth="0.7" />
                <rect x="14" y="12" width="32" height="5" rx="1" fill={accent} />
                <rect x="17" y="19.5" width="12" height="3.4" rx="1.7" fill="none" stroke={accent} strokeWidth="0.6" />
                <rect x="31" y="19.5" width="12" height="3.4" rx="1.7" fill="none" stroke={accent} strokeWidth="0.6" />
                <rect x="5" y="28.5" width="50" height="4.5" rx="1.5" fill="none" stroke={accent} strokeWidth="0.7" />
                <rect x="5" y="35.5" width="50" height="4.5" rx="1.5" fill="none" stroke={accent} strokeWidth="0.7" />
                <rect x="15" y="35.5" width="0.7" height="4.5" fill={accent} />
                <rect x="5" y="43" width="50" height="23" fill="none" stroke={accent} strokeWidth="0.9" />
                <rect x="5" y="47.5" width="50" height="0.8" fill={accent} />
                <rect x="14" y="43" width="0.7" height="23" fill={accent} />
                <rect x="36" y="43" width="0.7" height="23" fill={accent} />
                <rect x="44" y="43" width="0.7" height="23" fill={accent} />
                <rect x="5" y="68" width="50" height="4" rx="1.5" fill="none" stroke={accent} strokeWidth="0.7" />
                <rect x="14" y="68" width="0.7" height="4" fill={accent} />
                <rect x="6" y="76" width="16" height="0.8" fill={accent} />
                <rect x="38" y="76" width="16" height="0.8" fill={accent} />
            </svg>
        );
    }

    // The two-tone pad: angled bars in both inks, a filled column head.
    if (id === 'memoSplit') {
        return (
            <svg viewBox="0 0 60 80" width="46" height="62" aria-hidden="true">
                <rect x="0.5" y="0.5" width="59" height="79" rx="3" fill="#EAEAEA" stroke={line} />
                <polygon points="0.5,0.5 26,0.5 23,4 0.5,4" fill="#1C1815" />
                <polygon points="24,0.5 36,0.5 33,4 21,4" fill={accent} />
                <polygon points="40,2 59.5,0.8 59.5,12 42,11" fill="#1C1815" />
                <rect x="6" y="9" width="14" height="4" rx="0.6" fill="#1C1815" />
                <rect x="21" y="9" width="13" height="4" rx="0.6" fill={accent} />
                <rect x="6" y="15" width="26" height="1" fill="#1C1815" opacity="0.5" />
                <rect x="6" y="17.5" width="22" height="1" fill="#1C1815" opacity="0.5" />
                <rect x="0.5" y="21" width="59" height="0.9" fill="#1C1815" />
                <rect x="6" y="25" width="9" height="3.4" fill="#1C1815" />
                <rect x="17" y="25" width="37" height="3.4" fill="#FFFFFF" stroke={accent} strokeWidth="0.5" />
                <rect x="6" y="30" width="9" height="3.4" fill="#1C1815" />
                <rect x="17" y="30" width="37" height="3.4" fill="#FFFFFF" stroke={accent} strokeWidth="0.5" />
                <rect x="6" y="36" width="48" height="4.5" fill={accent} />
                <rect x="6" y="40.5" width="48" height="22" fill="#FFFFFF" stroke="#1C1815" strokeWidth="0.6" />
                <rect x="16" y="40.5" width="0.6" height="22" fill="#1C1815" />
                <rect x="34" y="40.5" width="0.6" height="22" fill="#1C1815" />
                <rect x="43" y="40.5" width="0.6" height="22" fill="#1C1815" />
                <rect x="34" y="57" width="9" height="5.5" fill="#1C1815" />
                <rect x="6" y="65" width="12" height="4" fill={accent} />
                <rect x="19" y="65" width="35" height="4" fill="#E2E2E2" stroke="#C9C9C9" strokeWidth="0.4" />
                <polygon points="0.5,74 26,74 23,79.5 0.5,79.5" fill={accent} />
                <polygon points="24,74 59.5,74 59.5,79.5 21,79.5" fill="#1C1815" />
            </svg>
        );
    }

    // The double-bordered pad: a heavy frame with a hairline inside it.
    if (id === 'memoBadge') {
        return (
            <svg viewBox="0 0 60 80" width="46" height="62" aria-hidden="true">
                <rect x="0.5" y="0.5" width="59" height="79" rx="2" fill="#FFFFFF" stroke={line} />
                <rect x="2" y="2" width="56" height="76" fill="none" stroke={accent} strokeWidth="3" />
                <rect x="5" y="5" width="50" height="70" fill="none" stroke={accent} strokeWidth="0.6" />
                <rect x="5.5" y="5.5" width="49" height="17" fill="#F4F4F4" />
                <rect x="7" y="7" width="11" height="3.2" rx="1.6" fill="#FFFFFF" stroke={accent} strokeWidth="0.5" />
                <rect x="12" y="12" width="36" height="5" rx="1" fill={accent} />
                <rect x="20" y="18.5" width="20" height="3" rx="1.5" fill="#FFFFFF" stroke={accent} strokeWidth="0.5" />
                <rect x="5.5" y="23" width="49" height="4" fill={accent} />
                <rect x="8" y="30" width="10" height="3" fill={accent} />
                <rect x="18" y="30" width="34" height="3" fill="#ECECEC" stroke={accent} strokeWidth="0.4" />
                <rect x="8" y="35" width="10" height="3" fill={accent} />
                <rect x="18" y="35" width="34" height="3" fill="#ECECEC" stroke={accent} strokeWidth="0.4" />
                <rect x="7.8" y="41" width="44.4" height="25" fill="none" stroke={accent} strokeWidth="0.7" />
                <rect x="7.8" y="41" width="44.4" height="4.5" fill="none" stroke={accent} strokeWidth="0.7" />
                <rect x="14" y="45.5" width="0.6" height="20.5" fill={accent} />
                <rect x="34" y="45.5" width="0.6" height="20.5" fill={accent} />
                <rect x="42" y="45.5" width="0.6" height="20.5" fill={accent} />
                <rect x="8" y="69" width="44" height="0.8" fill={accent} opacity="0.5" />
                <rect x="8" y="73" width="13" height="1.2" fill={accent} opacity="0.7" />
                <rect x="39" y="73" width="13" height="1.2" fill={accent} opacity="0.7" />
            </svg>
        );
    }

    // The one-ink pad: a grey heading, a solid band, a ruled grid.
    if (id === 'memoRuled') {
        return (
            <svg viewBox="0 0 60 80" width="46" height="62" aria-hidden="true">
                <rect x="0.5" y="0.5" width="59" height="79" rx="3" fill="#FFFFFF" stroke={line} />
                <rect x="0.5" y="0.5" width="59" height="22" fill="#F1F1F1" />
                <rect x="10" y="5" width="40" height="6" rx="1" fill={accent} />
                <rect x="20" y="13" width="20" height="4" rx="1" fill={accent} />
                <rect x="0.5" y="22" width="59" height="5" fill={accent} />
                <rect x="6" y="31" width="16" height="1.2" fill={accent} opacity="0.7" />
                <rect x="38" y="31" width="16" height="1.2" fill={accent} opacity="0.7" />
                <rect x="6" y="36" width="48" height="1" fill={accent} opacity="0.45" />
                <rect x="6" y="41" width="48" height="5" fill="#EFEFEF" stroke={accent} strokeWidth="0.5" />
                <rect x="5.8" y="41" width="48.4" height="26" fill="none" stroke={accent} strokeWidth="0.6" />
                <rect x="24" y="46" width="0.6" height="21" fill={accent} opacity="0.8" />
                <rect x="34" y="46" width="0.6" height="21" fill={accent} opacity="0.8" />
                <rect x="42" y="46" width="0.6" height="21" fill={accent} opacity="0.8" />
                <rect x="34" y="58" width="8.6" height="9" fill="#EFEFEF" stroke={accent} strokeWidth="0.5" />
                <rect x="6" y="70" width="48" height="1" fill={accent} opacity="0.45" />
                <rect x="6" y="75" width="14" height="1.2" fill={accent} opacity="0.7" />
                <rect x="40" y="75" width="14" height="1.2" fill={accent} opacity="0.7" />
            </svg>
        );
    }

    // The shop-header pad, the other printed design.
    if (id === 'memoShop') {
        return (
            <svg viewBox="0 0 60 80" width="46" height="62" aria-hidden="true">
                <defs>
                    <linearGradient id="memoShopPaper" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor="#FFFFFF" />
                        <stop offset="0.45" stopColor={SHOP_COLORS.paperMid} />
                        <stop offset="1" stopColor={SHOP_COLORS.paperFoot} />
                    </linearGradient>
                </defs>
                <rect x="0.5" y="0.5" width="59" height="79" rx="3" fill="url(#memoShopPaper)" stroke={line} />
                <rect x="24" y="4" width="12" height="3" rx="1.5" fill="none" stroke="#1A1A1A" strokeWidth="0.5" />
                <rect x="9" y="9" width="42" height="6" rx="1" fill={SHOP_COLORS.name} />
                <rect x="18" y="17" width="24" height="4" rx="2" fill={SHOP_COLORS.plate} />
                <rect x="13" y="23" width="34" height="1.6" fill={SHOP_COLORS.note} />
                <rect x="5" y="27" width="50" height="5" fill={SHOP_COLORS.band} />
                <rect x="38" y="34" width="8" height="3.4" fill={SHOP_COLORS.tag} />
                <rect x="46" y="34" width="9" height="3.4" fill="#FFFFFF" stroke={SHOP_COLORS.tag} strokeWidth="0.4" />
                <rect x="5" y="39" width="9" height="3.4" fill={SHOP_COLORS.tag} />
                <rect x="14" y="39" width="41" height="3.4" fill="#FFFFFF" stroke={SHOP_COLORS.tag} strokeWidth="0.4" />
                <rect x="5" y="45" width="50" height="5" fill={SHOP_COLORS.head} />
                <rect x="4.7" y="44.7" width="50.6" height="23" fill="none" stroke="#1A1A1A" strokeWidth="0.6" />
                <rect x="12" y="50" width="0.6" height="17.7" fill="#1A1A1A" />
                <rect x="36" y="50" width="0.6" height="17.7" fill="#1A1A1A" />
                <rect x="44" y="50" width="0.6" height="17.7" fill="#1A1A1A" />
                <rect x="44.6" y="50" width="10.7" height="17.7" fill="#CFE6F7" opacity="0.7" />
                <rect x="5" y="70" width="50" height="0.8" fill={line} />
                <rect x="5" y="75" width="14" height="1.4" fill="#1A1A1A" opacity="0.6" />
                <rect x="41" y="75" width="14" height="1.4" fill="#1A1A1A" opacity="0.6" />
            </svg>
        );
    }

    // The classic pad is a printed design of its own — cream stock, an orange
    // banner, red tags and a lime signature strip — so it gets its own drawing.
    if (id === 'memoClassic') {
        return (
            <svg viewBox="0 0 60 80" width="46" height="62" aria-hidden="true">
                <rect x="0.5" y="0.5" width="59" height="79" rx="3" fill={CLASSIC_COLORS.paper} stroke={line} />
                <path d="M0.5 3 a3 3 0 0 1 3 -2.5 H56.5 a3 3 0 0 1 3 2.5 V11 H0.5 Z" fill="#F7941E" />
                <path d="M0.5 11 C 12 7, 26 14, 38 10 C 48 7, 54 12, 59.5 9 V16 H0.5 Z" fill={CLASSIC_COLORS.paper} />
                <rect x="11" y="11" width="38" height="4" rx="1" fill={CLASSIC_COLORS.red} />
                <rect x="0.5" y="17" width="59" height="4" fill={accent} />
                <rect x="6" y="24" width="12" height="3.4" fill={CLASSIC_COLORS.red} />
                <rect x="18" y="24" width="36" height="3.4" fill="#F1F0EC" stroke={line} strokeWidth="0.4" />
                <rect x="6" y="29" width="12" height="3.4" fill={CLASSIC_COLORS.red} />
                <rect x="18" y="29" width="36" height="3.4" fill="#F1F0EC" stroke={line} strokeWidth="0.4" />
                <rect x="6" y="35" width="48" height="5" fill={accent} />
                <rect x="5.5" y="35" width="49" height="28" fill="none" stroke={line} />
                <rect x="13" y="40" width="0.8" height="23" fill={line} />
                <rect x="36" y="40" width="0.8" height="23" fill={line} />
                <rect x="45" y="40" width="0.8" height="23" fill={line} />
                <rect x="36" y="57" width="18" height="0.8" fill={line} />
                <rect x="6" y="67" width="48" height="0.9" fill={line} />
                <rect x="6" y="72" width="48" height="4" fill={CLASSIC_COLORS.lime} />
            </svg>
        );
    }

    // The classic pad is a printed design of its own — cream stock, an orange
    // banner, red tags and a lime signature strip — so it gets its own drawing.
    if (id === 'memoClassic') {
        return (
            <svg viewBox="0 0 60 80" width="46" height="62" aria-hidden="true">
                <rect x="0.5" y="0.5" width="59" height="79" rx="3" fill={CLASSIC_COLORS.paper} stroke={line} />
                <path d="M0.5 3 a3 3 0 0 1 3 -2.5 H56.5 a3 3 0 0 1 3 2.5 V11 H0.5 Z" fill="#F7941E" />
                <path d="M0.5 11 C 12 7, 26 14, 38 10 C 48 7, 54 12, 59.5 9 V16 H0.5 Z" fill={CLASSIC_COLORS.paper} />
                <rect x="11" y="11" width="38" height="4" rx="1" fill={CLASSIC_COLORS.red} />
                <rect x="0.5" y="17" width="59" height="4" fill={accent} />
                <rect x="6" y="24" width="12" height="3.4" fill={CLASSIC_COLORS.red} />
                <rect x="18" y="24" width="36" height="3.4" fill="#F1F0EC" stroke={line} strokeWidth="0.4" />
                <rect x="6" y="29" width="12" height="3.4" fill={CLASSIC_COLORS.red} />
                <rect x="18" y="29" width="36" height="3.4" fill="#F1F0EC" stroke={line} strokeWidth="0.4" />
                <rect x="6" y="35" width="48" height="5" fill={accent} />
                <rect x="5.5" y="35" width="49" height="28" fill="none" stroke={line} />
                <rect x="13" y="40" width="0.8" height="23" fill={line} />
                <rect x="36" y="40" width="0.8" height="23" fill={line} />
                <rect x="45" y="40" width="0.8" height="23" fill={line} />
                <rect x="36" y="57" width="18" height="0.8" fill={line} />
                <rect x="6" y="67" width="48" height="0.9" fill={line} />
                <rect x="6" y="72" width="48" height="4" fill={CLASSIC_COLORS.lime} />
            </svg>
        );
    }

    // Every pad in the catalogue draws itself above; nothing falls through.
    return null;
}

const toBnDigitsLocal = (n) => String(n).replace(/\d/g, (d) => '০১২৩৪৫৬৭৮৯'[d]);

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

/**
 * Shows the whole sheet at once at zoom 1 — scaled to fit the rail's width and
 * the height left on screen, nothing to scroll. Zooming in makes the sheet
 * larger than its window; it is then dragged around with the mouse (or finger).
 */
function ScaledSheet({ memo, zoom, onZoomChange }) {
    const boxRef = useRef(null);
    const sheetRef = useRef(null);
    const dragRef = useRef(null);
    const paper = paperSize(memo.paper);
    const [boxWidth, setBoxWidth] = useState(0);
    const [sheetHeight, setSheetHeight] = useState(paper.height);
    const [viewport, setViewport] = useState({ height: 900, wide: true });
    const [dragging, setDragging] = useState(false);

    useEffect(() => {
        const onResize = () => setViewport({ height: window.innerHeight, wide: window.innerWidth >= 1024 });
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        const box = boxRef.current;
        if (!box || typeof ResizeObserver === 'undefined') return undefined;
        const update = () => setBoxWidth(box.clientWidth);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(box);
        return () => observer.disconnect();
    }, []);

    // The sheet grows with its rows, so the wrapper follows its real height.
    useEffect(() => {
        const sheet = sheetRef.current;
        if (!sheet || typeof ResizeObserver === 'undefined') return undefined;
        const update = () => setSheetHeight(sheet.offsetHeight || paper.height);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(sheet);
        return () => observer.disconnect();
    }, [paper.height]);

    // What the card spends on its header, its padding and the bottom action bar.
    const chrome = 210;
    const availHeight = Math.max(240, viewport.height - chrome);
    const byWidth = boxWidth ? (boxWidth - 2) / paper.width : 0.6;
    const byHeight = viewport.wide ? availHeight / sheetHeight : Infinity;
    const fit = Math.min(1, Math.max(0.2, Math.min(byWidth, byHeight)));
    const scale = Math.min(fit * zoom, 3);
    const contentW = paper.width * scale;
    const contentH = sheetHeight * scale;
    const overflowing = contentW > boxWidth + 1 || (viewport.wide && contentH > availHeight + 1);

    const onPointerDown = (event) => {
        if (!overflowing || event.button > 0) return;
        const box = boxRef.current;
        dragRef.current = { x: event.clientX, y: event.clientY, left: box.scrollLeft, top: box.scrollTop };
        box.setPointerCapture?.(event.pointerId);
        setDragging(true);
    };
    const onPointerMove = (event) => {
        const drag = dragRef.current;
        if (!drag) return;
        const box = boxRef.current;
        box.scrollLeft = drag.left - (event.clientX - drag.x);
        box.scrollTop = drag.top - (event.clientY - drag.y);
    };
    const endDrag = () => {
        dragRef.current = null;
        setDragging(false);
    };
    const onWheel = (event) => {
        // Ctrl / ⌘ + wheel zooms, like a map; a plain wheel still scrolls the page.
        if (!(event.ctrlKey || event.metaKey)) return;
        event.preventDefault();
        onZoomChange((value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round((value + (event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP)) * 100) / 100)));
    };

    return (
        <div
            ref={boxRef}
            className={`w-full ${overflowing ? `overflow-auto ${dragging ? 'cursor-grabbing' : 'cursor-grab'} select-none touch-none` : ''}`}
            style={overflowing && viewport.wide ? { height: Math.min(availHeight, contentH + 2) } : undefined}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onWheel={onWheel}
        >
            <div
                className="overflow-hidden rounded-md shadow-[0_6px_28px_rgba(28,24,21,0.14)] ring-1 ring-black/5"
                style={{ width: contentW, height: contentH, margin: contentW < boxWidth ? '0 auto' : 0, flex: 'none' }}
            >
                <div ref={sheetRef} style={{ width: paper.width, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
                    <InvoiceDocument invoice={memo} />
                </div>
            </div>
        </div>
    );
}

export default function InvoiceDesignerGeneral() {
    const { t, currentLanguage } = useTranslation();
    const lang = currentLanguage === 'en' ? 'en' : 'bn';
    const logoInputRef = useRef(null);
    const watermarkInputRef = useRef(null);
    const dragFrom = useRef(null);
    const previewRef = useRef(null);

    const [memo, setMemo] = useState(() => loadDraft(lang, STORAGE_KEY));
    const [busy, setBusy] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [logoDragging, setLogoDragging] = useState(false);
    const [watermarkDragging, setWatermarkDragging] = useState(false);
    // Which column's grip is being held, and where its row started.
    const [dragArmed, setDragArmed] = useState(null);
    // What "Download" produces: the PDF by default, or a picture of the sheet.
    const [format, setFormat] = useState('pdf');

    // The pad — shop details and design both — is kept in this browser, so the
    // next print run starts from the same artwork.
    useEffect(() => {
        const timer = setTimeout(() => saveDraft(memo, STORAGE_KEY), 400);
        return () => clearTimeout(timer);
    }, [memo]);

    const patch = useCallback((changes) => setMemo((prev) => ({ ...prev, ...changes })), []);
    const patchShop = useCallback((changes) => setMemo((prev) => ({ ...prev, shop: { ...prev.shop, ...changes } })), []);
    const patchField = useCallback((key, value) => setMemo((prev) => ({ ...prev, fields: { ...prev.fields, [key]: value } })), []);
    const patchTotal = useCallback((key, value) => setMemo((prev) => ({ ...prev, totals: { ...prev.totals, [key]: value } })), []);
    const patchWatermark = useCallback((changes) => setMemo((prev) => ({ ...prev, watermark: { ...prev.watermark, ...changes } })), []);
    const patchShow = useCallback((key, value) => setMemo((prev) => ({ ...prev, show: { ...prev.show, [key]: value } })), []);
    const patchColumn = useCallback((id, changes) => setMemo((prev) => ({
        ...prev,
        columns: prev.columns.map((col) => (col.id === id ? { ...col, ...changes } : col)),
    })), []);
    /** Drag, or arrow-key, a column into a new place in the printed order. */
    const moveColumn = useCallback((from, to) => setMemo((prev) => {
        if (to < 0 || to >= prev.columns.length || from === to) return prev;
        const columns = [...prev.columns];
        const [moved] = columns.splice(from, 1);
        columns.splice(to, 0, moved);
        return { ...prev, columns };
    }), []);
    const addColumn = () => setMemo((prev) => (prev.columns.length >= MAX_COLUMNS
        ? prev
        : { ...prev, columns: [...prev.columns, newColumn()] }));
    const removeColumn = (id) => setMemo((prev) => (prev.columns.length <= MIN_COLUMNS
        ? prev
        : { ...prev, columns: prev.columns.filter((col) => col.id !== id) }));

    /** Reads a picked picture and hands the data URL to `apply` — logo or watermark. */
    const acceptImage = async (file, apply) => {
        if (!file) return;
        // Some browsers leave the MIME type empty for HEIC/AVIF, so the name counts too.
        const looksLikeImage = file.type.startsWith('image/') || /\.(jpe?g|png|webp|avif|heic|heif)$/i.test(file.name);
        if (!looksLikeImage || file.size > MAX_LOGO_BYTES) {
            notifications.show({ title: t('common.error'), message: t('invoiceDesigner.errors.logo'), color: 'red' });
            return;
        }
        try {
            // HEIC is turned into a JPEG first; every other format the browser draws as is.
            const drawable = await toDisplayableBlob(file);
            // The watermark prints across the page, so it is kept larger than the logo.
            apply(await readLogoFile(drawable, apply === applyWatermarkImage ? 700 : 360));
        } catch {
            notifications.show({ title: t('common.error'), message: t('invoiceDesigner.errors.logo'), color: 'red' });
        }
    };
    const applyShopLogo = useCallback((logo) => patchShop({ logo }), [patchShop]);
    const applyWatermarkImage = useCallback((image) => patchWatermark({ image, mode: 'image' }), [patchWatermark]);

    const handleLogo = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        void acceptImage(file, applyShopLogo);
    };
    const handleLogoDrop = (event) => {
        event.preventDefault();
        setLogoDragging(false);
        void acceptImage(event.dataTransfer.files?.[0], applyShopLogo);
    };
    const handleWatermarkDrop = (event) => {
        event.preventDefault();
        setWatermarkDragging(false);
        void acceptImage(event.dataTransfer.files?.[0], applyWatermarkImage);
    };
    const handleWatermarkFile = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        void acceptImage(file, applyWatermarkImage);
    };

    /**
     * Start over: a blank pad in the same language.
     *
     * It throws away the shop's whole letterhead — name, logo, watermark, the
     * columns they renamed — and the draft is saved over a moment later, so it
     * is asked about first.
     */
    const [confirmReset, setConfirmReset] = useState(false);
    const handleReset = () => {
        setMemo(defaultMemo(lang));
        setConfirmReset(false);
    };

    // The free-picker tile wears the colour it is holding, so its icon has to
    // flip to white once that colour is dark enough to swallow a grey one.
    const isCustomAccent = !ACCENT_PRESETS.some((c) => c.toLowerCase() === memo.accent.toLowerCase());
    const customAccentIsLight = useMemo(() => {
        const hex = memo.accent.replace('#', '');
        if (hex.length !== 6) return true;
        const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
        return (0.299 * r + 0.587 * g + 0.114 * b) > 150;
    }, [memo.accent]);

    const hasInput = Boolean(memo.shop.name || memo.shop.tagline || memo.shop.address || memo.shop.logo);
    const status = deriveToolStatus({ hasInput, canRun: Boolean(memo.shop.name), hasResult: false });
    const statusText = getToolStatusText(status, t, {
        ready: t('invoiceDesigner.statusReady'),
    });

    const getSheet = () => previewRef.current?.querySelector('[data-invoice-sheet]');

    const runExport = async (kind) => {
        const sheet = getSheet();
        if (!sheet || busy) return;
        setBusy(kind);
        try {
            // A pad leaving as a file or going to the printer is the tool running,
            // so both count — the design itself is edited continuously and would
            // otherwise never register a use.
            recordToolUsage('general/invoice-designer', { category: 'general' });
            const canvas = await captureInvoiceCanvas(sheet);
            const base = `cash-memo-${(memo.shop.name || 'blank').replace(/[^\w-]+/g, '_').slice(0, 40) || 'blank'}`;
            if (kind === 'download' && format !== 'pdf') {
                // `ext` is what the file REALLY is: a browser that cannot write the
                // chosen format falls back inside the encoder.
                const out = await encodeCanvas(canvas, format, { quality: 0.95, background: '#ffffff' });
                downloadBlob(out.blob, `${base}.${out.ext}`);
            } else if (kind === 'download') {
                downloadBlob(canvasToPdfBlob(canvas), `${base}.pdf`);
            } else {
                await printBlob(canvasToPdfBlob(canvas));
            }
            if (kind !== 'print') {
                notifications.show({ title: t('common.success'), message: t('invoiceDesigner.done'), color: 'green' });
            }
        } catch {
            notifications.show({ title: t('common.error'), message: t('invoiceDesigner.errors.export'), color: 'red' });
        } finally {
            setBusy(null);
        }
    };

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`invoiceDesigner.howTo.step${i}`)),
        [t],
    );

    return (
        <ToolPageLayout
            {...PAGE_PROPS}
            subtitleKey="invoiceDesigner.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('invoiceDesigner.contentTitle')}
            howToSteps={howToSteps}
            // The sheet is the rail on the right and stays pinned while the left
            // column scrolls, so every change is visible as it is made. The 9px is
            // half the column gap.
            variant="wideControls"
            railWidthClass="lg:w-[calc(48%_-_9px)]"
            onReset={hasInput ? () => setConfirmReset(true) : undefined}
            resetLabel={t('invoiceDesigner.newInvoice')}
            primaryAction={{
                label: t('toolPage.downloadAs', { format: t(`toolPage.outputFormat.formats.${format}`) }),
                onClick: () => runExport('download'),
                disabled: busy !== null,
                loading: busy === 'download',
                icon: IconDownload,
            }}
            actionExtra={(
                <OutputFormatSelect
                    formats={PAGE_OUTPUT_FORMATS}
                    value={format}
                    onChange={setFormat}
                    disabled={busy !== null}
                />
            )}
            secondaryAction={[
                {
                    key: 'print',
                    label: busy === 'print' ? t('invoiceDesigner.printing') : t('invoiceDesigner.print'),
                    icon: IconPrinter,
                    onClick: () => runExport('print'),
                    disabled: busy !== null,
                },
            ]}
            controls={(
                <div className="space-y-3">
                    <ToolCard title={t('invoiceDesigner.shopTitle')}>
                        <div className="space-y-2.5">
                            <input ref={logoInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.avif,.heic,.heif" className="hidden" onChange={handleLogo} />
                            {memo.shop.logo ? (
                                // The logo as it will print, with what you can do to it beside it.
                                <div
                                    onDragOver={(e) => { e.preventDefault(); setLogoDragging(true); }}
                                    onDragLeave={() => setLogoDragging(false)}
                                    onDrop={handleLogoDrop}
                                    className={`flex items-center gap-3.5 rounded-xl border p-3 transition-colors ${
                                        logoDragging
                                            ? 'border-amber-500 bg-amber-50/70 dark:border-amber-500 dark:bg-amber-900/20'
                                            : 'border-[#ECE8E4] bg-[#FAF8F6] dark:border-zinc-700 dark:bg-zinc-800/50'
                                    }`}
                                >
                                    <span className="flex h-16 w-24 flex-none items-center justify-center overflow-hidden rounded-lg border border-[#ECE8E4] bg-white p-1.5 dark:border-zinc-700">
                                        <img src={memo.shop.logo} alt="" className="max-h-full max-w-full object-contain" />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">{t('invoiceDesigner.logoAdded')}</p>
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                onClick={() => logoInputRef.current?.click()}
                                                className="inline-flex items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#3D3833] transition-colors hover:border-amber-400/60 hover:text-amber-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                            >
                                                <IconRefresh size={14} stroke={2} />
                                                {t('invoiceDesigner.logoChange')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => patchShop({ logo: '' })}
                                                className="inline-flex items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#6B635C] transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-red-950/30"
                                            >
                                                <IconTrash size={14} stroke={2} />
                                                {t('invoiceDesigner.logoRemove')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                // A drop zone that also answers a click — a dashed box that only
                                // did one of them would read as broken.
                                <button
                                    type="button"
                                    onClick={() => logoInputRef.current?.click()}
                                    onDragOver={(e) => { e.preventDefault(); setLogoDragging(true); }}
                                    onDragLeave={() => setLogoDragging(false)}
                                    onDrop={handleLogoDrop}
                                    className={`flex w-full items-center gap-3.5 rounded-xl border border-dashed p-3.5 text-left transition-colors ${
                                        logoDragging
                                            ? 'border-amber-500 bg-amber-50/70 dark:border-amber-500 dark:bg-amber-900/20'
                                            : 'border-[#DCD6D0] bg-[#FAF8F6] hover:border-amber-400/70 hover:bg-amber-50/40 dark:border-zinc-600 dark:bg-zinc-800/40'
                                    }`}
                                >
                                    <span className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-white text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.06)] dark:bg-zinc-900">
                                        <IconUpload size={18} stroke={2} />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                            {t('invoiceDesigner.logoDrop')}
                                        </span>
                                        <span className="mt-0.5 block text-[11.5px] text-[#A8A099] dark:text-zinc-500">{t('invoiceDesigner.logoHint')}</span>
                                    </span>
                                </button>
                            )}
                            <Field label={t('invoiceDesigner.businessName')}>
                                <input className={inputClass} value={memo.shop.name} onChange={(e) => patchShop({ name: e.target.value })} />
                            </Field>
                            {/* The line every printed memo carries under the shop name. */}
                            <Field label={t('invoiceDesigner.taglineLabel')} hint={t('invoiceDesigner.taglineHint')}>
                                <input
                                    className={inputClass}
                                    value={memo.shop.tagline}
                                    placeholder={t('invoiceDesigner.taglinePlaceholder')}
                                    onChange={(e) => patchShop({ tagline: e.target.value })}
                                />
                            </Field>
                            <Field label={t('invoiceDesigner.address')}>
                                <textarea rows={2} className={`${inputClass} resize-y`} value={memo.shop.address} onChange={(e) => patchShop({ address: e.target.value })} />
                            </Field>
                            <ContactFields value={memo.shop} onChange={patchShop} t={t} />
                        </div>
                    </ToolCard>

                    {/* The lines every pad's header carries — and a switch for
                        each of them, so a shop that wants none keeps the plain
                        heading. */}
                    <ToolCard title={t('invoiceDesigner.classicTitle')} collapsible defaultOpen={false}>
                            <div className="space-y-2.5">
                                <Field label={t('invoiceDesigner.proprietorLabel')} hint={t('invoiceDesigner.proprietorHint')}>
                                    <input
                                        className={inputClass}
                                        value={memo.proprietor}
                                        placeholder={t('invoiceDesigner.proprietorPlaceholder')}
                                        onChange={(e) => patch({ proprietor: e.target.value })}
                                    />
                                </Field>
                                <Field label={t('invoiceDesigner.distributorLabel')} hint={t('invoiceDesigner.distributorHint')}>
                                    <input
                                        className={inputClass}
                                        value={memo.distributor}
                                        placeholder={t('invoiceDesigner.distributorPlaceholder')}
                                        onChange={(e) => patch({ distributor: e.target.value })}
                                    />
                                </Field>
                                <div className="grid gap-2.5 sm:grid-cols-2">
                                    <Field label={t('invoiceDesigner.bismillahLabel')}>
                                        <input
                                            className={inputClass}
                                            value={memo.bismillah}
                                            placeholder={t('invoiceDesigner.bismillahPlaceholder')}
                                            onChange={(e) => patch({ bismillah: e.target.value })}
                                        />
                                    </Field>
                                    <Field label={t('invoiceDesigner.sloganLabel')} hint={t('invoiceDesigner.sloganHint')}>
                                        <input
                                            className={inputClass}
                                            value={memo.slogan}
                                            placeholder={t('invoiceDesigner.sloganPlaceholder')}
                                            onChange={(e) => patch({ slogan: e.target.value })}
                                        />
                                    </Field>
                                </div>
                                <div>
                                    <span className={labelClass}>{t('invoiceDesigner.showLabel')}</span>
                                    <div className="flex flex-wrap gap-1.5">
                                        {CLASSIC_SHOW.map((key) => (
                                            <ChipToggle
                                                key={key}
                                                label={t(`invoiceDesigner.show.${key}`)}
                                                checked={memo.show[key] !== false}
                                                onChange={(value) => patchShow(key, value)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                    </ToolCard>

                    <ToolCard title={t('invoiceDesigner.formTitle')} collapsible defaultOpen={false}>
                        <div className="space-y-3.5">
                            <div className="grid gap-2.5 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)]">
                                <Field label={t('invoiceDesigner.titleLabel')}>
                                    <input
                                        className={inputClass}
                                        value={memo.title}
                                        placeholder={t('invoiceDesigner.titlePlaceholder')}
                                        onChange={(e) => patch({ title: e.target.value })}
                                    />
                                </Field>
                                <Field label={t('invoiceDesigner.paperLabel')}>
                                    <Select value={memo.paper} onValueChange={(value) => patch({ paper: value })}>
                                        <SelectTrigger className="h-[38px] w-full rounded-lg border-[#ECE8E4] text-[13px] dark:border-zinc-700">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {PAPER_IDS.map((id) => (
                                                <SelectItem key={id} value={id} className="text-[13px]">
                                                    {t(`invoiceDesigner.papers.${id}`)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label={t('invoiceDesigner.rowsLabel')}>
                                    <RowStepper
                                        value={memo.rows}
                                        onChange={(value) => patch({ rows: value })}
                                        decLabel={t('invoiceDesigner.rowsLess')}
                                        incLabel={t('invoiceDesigner.rowsMore')}
                                        lang={lang}
                                    />
                                </Field>
                            </div>

                            <div>
                                <span className={labelClass}>{t('invoiceDesigner.columnsLabel')}</span>
                                {/* The pad's own column heads. A tailor wants মাপ, a
                                    pharmacy wants ঔষধের নাম — so they are typed, not fixed. */}
                                <div className="space-y-2">
                                    {memo.columns.map((col, i) => (
                                        <div
                                            key={col.id}
                                            // The row is only draggable while the grip is
                                            // held, or a drag would start on every attempt
                                            // to select text in the name field.
                                            draggable={dragArmed === col.id}
                                            onDragStart={(e) => { dragFrom.current = i; e.dataTransfer.effectAllowed = 'move'; }}
                                            onDragOver={(e) => {
                                                if (dragFrom.current === null || dragFrom.current === i) return;
                                                e.preventDefault();
                                                moveColumn(dragFrom.current, i);
                                                dragFrom.current = i;
                                            }}
                                            onDragEnd={() => { dragFrom.current = null; setDragArmed(null); }}
                                            className={`flex items-center gap-2 rounded-lg transition-opacity ${
                                                dragArmed === col.id ? 'opacity-70' : ''
                                            }`}
                                        >
                                            <Hint label={t('invoiceDesigner.reorderColumn')}>
                                                <button
                                                    type="button"
                                                    aria-label={t('invoiceDesigner.reorderColumn')}
                                                    onPointerDown={() => setDragArmed(col.id)}
                                                    onPointerUp={() => setDragArmed(null)}
                                                    // Keyboard users move a column with the
                                                    // arrows rather than a mouse drag.
                                                    onKeyDown={(e) => {
                                                        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
                                                        e.preventDefault();
                                                        moveColumn(i, i + (e.key === 'ArrowDown' ? 1 : -1));
                                                    }}
                                                    className="flex h-[38px] w-6 flex-none cursor-grab items-center justify-center text-[#C4BDB5] transition-colors hover:text-[#6B635C] active:cursor-grabbing dark:text-zinc-600 dark:hover:text-zinc-300"
                                                >
                                                    <IconGripVertical size={15} stroke={2} />
                                                </button>
                                            </Hint>
                                            <span className="w-4 flex-none text-center text-[12px] font-semibold text-[#A8A099]">
                                                {lang === 'bn' ? toBnDigitsLocal(i + 1) : i + 1}
                                            </span>
                                            <input
                                                className={inputClass}
                                                value={col.label}
                                                placeholder={t('invoiceDesigner.columnPlaceholder')}
                                                onChange={(e) => patchColumn(col.id, { label: e.target.value })}
                                            />
                                            <Hint label={t('invoiceDesigner.columnWidth')}>
                                                <input
                                                    type="range"
                                                    min="0.5"
                                                    max="4"
                                                    step="0.1"
                                                    aria-label={t('invoiceDesigner.columnWidth')}
                                                    value={col.flex}
                                                    onChange={(e) => patchColumn(col.id, { flex: Number(e.target.value) })}
                                                    className="h-[38px] w-20 flex-none accent-amber-500"
                                                />
                                            </Hint>
                                            <Hint label={t('invoiceDesigner.removeColumn')}>
                                                <button
                                                    type="button"
                                                    aria-label={t('invoiceDesigner.removeColumn')}
                                                    disabled={memo.columns.length <= MIN_COLUMNS}
                                                    onClick={() => removeColumn(col.id)}
                                                    className="flex h-[38px] w-9 flex-none items-center justify-center rounded-lg border border-[#ECE8E4] text-[#6B635C] transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-40 dark:border-zinc-700 dark:text-zinc-300"
                                                >
                                                    <IconTrash size={15} stroke={2} />
                                                </button>
                                            </Hint>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={addColumn}
                                    disabled={memo.columns.length >= MAX_COLUMNS}
                                    className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-[#DCD6D0] px-3 py-1.5 text-[12px] font-semibold text-[#6B635C] transition-colors hover:border-amber-400 hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-zinc-600 dark:text-zinc-300"
                                >
                                    <IconPlus size={14} stroke={2.2} />
                                    {t('invoiceDesigner.addColumn')}
                                </button>
                            </div>

                            {/* The blanks at the top and the boxes at the foot are
                                two short lists; side by side they read as one row
                                of switches rather than two stacked paragraphs. */}
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                    <span className={labelClass}>{t('invoiceDesigner.fieldsLabel')}</span>
                                    <div className="flex flex-wrap gap-1.5">
                                        {['memoNo', 'date', 'name', 'address'].map((key) => (
                                            <ChipToggle
                                                key={key}
                                                label={t(`invoiceDesigner.fields.${key}`)}
                                                checked={memo.fields[key]}
                                                onChange={(value) => patchField(key, value)}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <span className={labelClass}>{t('invoiceDesigner.totalsLabel')}</span>
                                    <div className="flex flex-wrap gap-1.5">
                                        {['total', 'advance', 'due'].map((key) => (
                                            <ChipToggle
                                                key={key}
                                                label={t(`invoiceDesigner.totalsFields.${key}`)}
                                                checked={memo.totals[key]}
                                                onChange={(value) => patchTotal(key, value)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <Field label={t('invoiceDesigner.footerNote')} hint={t('invoiceDesigner.footerNoteHint')}>
                                <input
                                    className={inputClass}
                                    value={memo.footerNote}
                                    placeholder={t('invoiceDesigner.footerNotePlaceholder')}
                                    onChange={(e) => patch({ footerNote: e.target.value })}
                                />
                            </Field>

                            <div className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
                                <ToggleRow
                                    label={t('invoiceDesigner.rowLines')}
                                    checked={memo.rowLines}
                                    onChange={(value) => patch({ rowLines: value })}
                                />
                                <ToggleRow
                                    label={t('invoiceDesigner.columnLines')}
                                    checked={memo.columnLines}
                                    onChange={(value) => patch({ columnLines: value })}
                                />
                                <ToggleRow
                                    label={t('invoiceDesigner.showWords')}
                                    checked={memo.showWords}
                                    onChange={(value) => patch({ showWords: value })}
                                />
                                <ToggleRow
                                    label={t('invoiceDesigner.showSignature')}
                                    checked={memo.showSignature}
                                    onChange={(value) => patch({ showSignature: value })}
                                />
                            </div>
                        </div>
                    </ToolCard>
                </div>
            )}
            settings={(
                <div className="space-y-3">
                    <ToolCard title={t('invoiceDesigner.designTitle')} collapsible defaultOpen={false}>
                        <div className="space-y-3.5">
                            <div>
                                <span className={labelClass}>{t('invoiceDesigner.templateLabel')}</span>
                                <div className="grid grid-cols-5 gap-1.5">
                                    {TEMPLATES.map((id) => (
                                        <Hint label={t(`invoiceDesigner.templates.${id}`)} key={id}>
                                            <button
                                                type="button"
                                                onClick={() => patch({ template: id })}
                                                aria-pressed={memo.template === id}
                                                aria-label={t(`invoiceDesigner.templates.${id}`)}
                                                className={`flex flex-col items-center gap-1 rounded-xl border p-1.5 transition-colors ${
                                                    memo.template === id
                                                        ? 'border-amber-500 bg-amber-50 dark:border-amber-500 dark:bg-amber-900/20'
                                                        : 'border-[#ECE8E4] bg-white hover:border-amber-400/50 dark:border-zinc-700 dark:bg-zinc-900'
                                                }`}
                                            >
                                                <TemplateThumb id={id} accent={memo.accent} active={memo.template === id} />
                                                <span className="text-[10.5px] font-semibold leading-tight text-[#3D3833] dark:text-zinc-300">
                                                    {t(`invoiceDesigner.templates.${id}`)}
                                                </span>
                                            </button>
                                        </Hint>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <span className={labelClass}>{t('invoiceDesigner.accentLabel')}</span>
                                {/* Same tile grid the background picker uses: nineteen
                                    colours and the free picker. The padding leaves room
                                    for the active tile's scale-up. */}
                                <div className="-mx-1 grid grid-cols-[repeat(auto-fill,minmax(44px,1fr))] gap-2 p-1 pt-0.5">
                                    {ACCENT_PRESETS.map((color) => {
                                        const active = memo.accent.toLowerCase() === color.toLowerCase();
                                        return (
                                            <Hint label={color.toUpperCase()} key={color}>
                                                <button
                                                    type="button"
                                                    onClick={() => patch({ accent: color })}
                                                    aria-label={color}
                                                    aria-pressed={active}
                                                    className={`aspect-square w-full rounded-lg border-2 transition-transform ${
                                                        active
                                                            ? 'scale-105 border-amber-500'
                                                            : 'border-[#ECE8E4] hover:scale-105 dark:border-zinc-600'
                                                    }`}
                                                    style={{ backgroundColor: color }}
                                                />
                                            </Hint>
                                        );
                                    })}
                                    <Hint label={t('invoiceDesigner.customColor')}>
                                        <label
                                            aria-label={t('invoiceDesigner.customColor')}
                                            className={`relative flex aspect-square w-full cursor-pointer items-center justify-center rounded-lg border-2 transition-transform ${
                                                isCustomAccent
                                                    ? 'scale-105 border-amber-500'
                                                    : 'border-[#ECE8E4] hover:scale-105 dark:border-zinc-600'
                                            }`}
                                            style={isCustomAccent ? { backgroundColor: memo.accent } : undefined}
                                        >
                                            <IconColorPicker
                                                size={15}
                                                stroke={2}
                                                className={isCustomAccent && !customAccentIsLight
                                                    ? 'text-white'
                                                    : 'text-[#6B635C] dark:text-zinc-300'}
                                            />
                                            <input
                                                type="color"
                                                value={memo.accent}
                                                aria-label={t('invoiceDesigner.customColor')}
                                                onChange={(e) => patch({ accent: e.target.value })}
                                                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                            />
                                        </label>
                                    </Hint>
                                </div>
                            </div>

                            <div>
                                <span className={labelClass}>{t('invoiceDesigner.docLangLabel')}</span>
                                <div className="grid h-12 grid-cols-2 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                    {['bn', 'en'].map((id) => (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() => patch({ docLang: id })}
                                            aria-pressed={memo.docLang === id}
                                            className={`flex items-center justify-center rounded-lg text-[12.5px] font-semibold leading-none transition-all ${
                                                memo.docLang === id
                                                    ? 'bg-white text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.06)] dark:bg-zinc-900 dark:text-amber-400'
                                                    : 'text-[#6B635C] dark:text-zinc-400'
                                            }`}
                                        >
                                            {t(`invoiceDesigner.docLang.${id}`)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <p className="text-[11.5px] leading-relaxed text-[#A8A099] dark:text-zinc-500">
                                {t('invoiceDesigner.savedHint')}
                            </p>
                        </div>
                    </ToolCard>

                    {/* Its own card: the watermark is a layer of its own on the
                        pad, with its own picture, not another design knob. */}
                    <ToolCard title={t('invoiceDesigner.watermarkLabel')} collapsible defaultOpen={false}>
                        <div className="space-y-2.5">
                            <input
                                ref={watermarkInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.avif,.heic,.heif"
                                className="hidden"
                                onChange={handleWatermarkFile}
                            />
                            {/* A segmented control, like the language switch: the
                                three modes are one choice, not three flags. */}
                            <div className="grid h-10 grid-cols-3 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                {WATERMARK_MODES.map((mode) => (
                                    <button
                                        key={mode}
                                        type="button"
                                        onClick={() => patchWatermark({ mode })}
                                        aria-pressed={memo.watermark.mode === mode}
                                        className={`flex items-center justify-center rounded-lg text-[12.5px] font-semibold leading-none transition-all ${
                                            memo.watermark.mode === mode
                                                ? 'bg-white text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.06)] dark:bg-zinc-900 dark:text-amber-400'
                                                : 'text-[#6B635C] dark:text-zinc-400'
                                        }`}
                                    >
                                        {t(`invoiceDesigner.watermarkModes.${mode}`)}
                                    </button>
                                ))}
                            </div>

                            {memo.watermark.mode === 'none' ? (
                                <p className="text-[11.5px] leading-snug text-[#A8A099] dark:text-zinc-500">
                                    {t('invoiceDesigner.watermarkHint')}
                                </p>
                            ) : null}

                            {memo.watermark.mode === 'text' ? (
                                <Field label={t('invoiceDesigner.watermarkText')}>
                                    <input
                                        className={inputClass}
                                        value={memo.watermark.text}
                                        placeholder={memo.shop.name || t('invoiceDesigner.watermarkTextPlaceholder')}
                                        onChange={(e) => patchWatermark({ text: e.target.value })}
                                    />
                                </Field>
                            ) : null}

                            {memo.watermark.mode === 'image' ? (
                                (memo.watermark.image || memo.shop.logo) ? (
                                    <div
                                        onDragOver={(e) => { e.preventDefault(); setWatermarkDragging(true); }}
                                        onDragLeave={() => setWatermarkDragging(false)}
                                        onDrop={handleWatermarkDrop}
                                        className={`flex items-center gap-3.5 rounded-xl border p-3 transition-colors ${
                                            watermarkDragging
                                                ? 'border-amber-500 bg-amber-50/70 dark:border-amber-500 dark:bg-amber-900/20'
                                                : 'border-[#ECE8E4] bg-[#FAF8F6] dark:border-zinc-700 dark:bg-zinc-800/50'
                                        }`}
                                    >
                                        <span className="flex h-16 w-24 flex-none items-center justify-center overflow-hidden rounded-lg border border-[#ECE8E4] bg-white p-1.5 dark:border-zinc-700">
                                            <img src={memo.watermark.image || memo.shop.logo} alt="" className="max-h-full max-w-full object-contain" />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                                {memo.watermark.image
                                                    ? t('invoiceDesigner.watermarkOwnImage')
                                                    : t('invoiceDesigner.watermarkUsingLogo')}
                                            </p>
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => watermarkInputRef.current?.click()}
                                                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#3D3833] transition-colors hover:border-amber-400/60 hover:text-amber-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                                >
                                                    <IconRefresh size={14} stroke={2} />
                                                    {t('invoiceDesigner.logoChange')}
                                                </button>
                                                {memo.watermark.image ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => patchWatermark({ image: '' })}
                                                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#6B635C] transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-red-950/30"
                                                    >
                                                        <IconTrash size={14} stroke={2} />
                                                        {t('invoiceDesigner.logoRemove')}
                                                    </button>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => watermarkInputRef.current?.click()}
                                        onDragOver={(e) => { e.preventDefault(); setWatermarkDragging(true); }}
                                        onDragLeave={() => setWatermarkDragging(false)}
                                        onDrop={handleWatermarkDrop}
                                        className={`flex w-full items-center gap-3.5 rounded-xl border border-dashed p-3.5 text-left transition-colors ${
                                            watermarkDragging
                                                ? 'border-amber-500 bg-amber-50/70 dark:border-amber-500 dark:bg-amber-900/20'
                                                : 'border-[#DCD6D0] bg-[#FAF8F6] hover:border-amber-400/70 hover:bg-amber-50/40 dark:border-zinc-600 dark:bg-zinc-800/40'
                                        }`}
                                    >
                                        <span className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-white text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.06)] dark:bg-zinc-900">
                                            <IconUpload size={18} stroke={2} />
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                                {t('invoiceDesigner.watermarkUpload')}
                                            </span>
                                            <span className="mt-0.5 block text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                                {t('invoiceDesigner.logoHint')}
                                            </span>
                                        </span>
                                    </button>
                                )
                            ) : null}

                            {memo.watermark.mode !== 'none' ? (
                                <div className="grid gap-2.5 sm:grid-cols-3">
                                    <SliderRow
                                        label={t('invoiceDesigner.watermarkOpacity')}
                                        value={memo.watermark.opacity}
                                        min={2}
                                        max={40}
                                        suffix="%"
                                        onChange={(value) => patchWatermark({ opacity: value })}
                                        lang={lang}
                                    />
                                    <SliderRow
                                        label={t('invoiceDesigner.watermarkSize')}
                                        value={memo.watermark.size}
                                        min={20}
                                        max={100}
                                        suffix="%"
                                        onChange={(value) => patchWatermark({ size: value })}
                                        lang={lang}
                                    />
                                    <SliderRow
                                        label={t('invoiceDesigner.watermarkAngle')}
                                        value={memo.watermark.angle}
                                        min={-90}
                                        max={90}
                                        suffix="°"
                                        onChange={(value) => patchWatermark({ angle: value })}
                                        lang={lang}
                                    />
                                    {/* Where it sits on the sheet, so it can be slid
                                        off the writing area. */}
                                    <SliderRow
                                        label={t('invoiceDesigner.watermarkX')}
                                        value={memo.watermark.x}
                                        min={0}
                                        max={100}
                                        suffix="%"
                                        onChange={(value) => patchWatermark({ x: value })}
                                        lang={lang}
                                    />
                                    <SliderRow
                                        label={t('invoiceDesigner.watermarkY')}
                                        value={memo.watermark.y}
                                        min={0}
                                        max={100}
                                        suffix="%"
                                        onChange={(value) => patchWatermark({ y: value })}
                                        lang={lang}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => patchWatermark({ x: 50, y: 50, angle: -24 })}
                                        className="mt-1 inline-flex h-[30px] items-center justify-center gap-1.5 self-end rounded-lg border border-[#ECE8E4] px-3 text-[12px] font-semibold text-[#6B635C] transition-colors hover:border-amber-400 hover:text-amber-700 dark:border-zinc-700 dark:text-zinc-300"
                                    >
                                        <IconRefresh size={14} stroke={2} />
                                        {t('invoiceDesigner.watermarkCentre')}
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </ToolCard>
                </div>
            )}
            preview={(
                // A plain card, not the shared preview panel: the panel keeps its own
                // fixed height and clips, and the pad has to show in full.
                <div className="flex flex-col overflow-hidden rounded-[20px] border border-[#ECE8E4] bg-white shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:border-zinc-700 dark:bg-zinc-900 lg:sticky lg:top-4">
                    <div className="flex items-center justify-between gap-3 border-b border-[#ECE8E4] px-4 py-2.5 dark:border-zinc-700">
                        <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                            {t('invoiceDesigner.previewTitle')}
                        </span>
                        <div className="flex items-center gap-1">
                            <Hint label={t('invoiceDesigner.zoomOut')}>
                                <button
                                    type="button"
                                    aria-label={t('invoiceDesigner.zoomOut')}
                                    disabled={zoom <= MIN_ZOOM}
                                    onClick={() => setZoom((z) => Math.max(MIN_ZOOM, Math.round((z - ZOOM_STEP) * 100) / 100))}
                                    className="flex h-7 w-7 items-center justify-center rounded-md text-[#6B635C] transition-colors hover:bg-[#F4F1ED] disabled:opacity-40 dark:text-zinc-300 dark:hover:bg-zinc-800"
                                >
                                    <IconZoomOut size={15} stroke={2} />
                                </button>
                            </Hint>
                            <span className="min-w-[44px] text-center text-[11.5px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                {lang === 'bn' ? toBnDigitsLocal(Math.round(zoom * 100)) : Math.round(zoom * 100)}%
                            </span>
                            <Hint label={t('invoiceDesigner.zoomIn')}>
                                <button
                                    type="button"
                                    aria-label={t('invoiceDesigner.zoomIn')}
                                    disabled={zoom >= MAX_ZOOM}
                                    onClick={() => setZoom((z) => Math.min(MAX_ZOOM, Math.round((z + ZOOM_STEP) * 100) / 100))}
                                    className="flex h-7 w-7 items-center justify-center rounded-md text-[#6B635C] transition-colors hover:bg-[#F4F1ED] disabled:opacity-40 dark:text-zinc-300 dark:hover:bg-zinc-800"
                                >
                                    <IconZoomIn size={15} stroke={2} />
                                </button>
                            </Hint>
                            <Hint label={t('invoiceDesigner.zoomReset')}>
                                <button
                                    type="button"
                                    aria-label={t('invoiceDesigner.zoomReset')}
                                    onClick={() => setZoom(1)}
                                    className="flex h-7 w-7 items-center justify-center rounded-md text-[#6B635C] transition-colors hover:bg-[#F4F1ED] dark:text-zinc-300 dark:hover:bg-zinc-800"
                                >
                                    <IconZoomReset size={15} stroke={2} />
                                </button>
                            </Hint>
                        </div>
                    </div>
                    <div ref={previewRef} className="flex justify-center bg-[#F7F5F3] p-3 dark:bg-zinc-950">
                        <ScaledSheet memo={memo} zoom={zoom} onZoomChange={setZoom} />
                    </div>

                    <ConfirmDialog
                        open={confirmReset}
                        onClose={() => setConfirmReset(false)}
                        onConfirm={handleReset}
                        title={t('invoiceDesigner.resetConfirmTitle')}
                        description={t('invoiceDesigner.resetConfirmBody')}
                        confirmLabel={t('invoiceDesigner.resetConfirmAction')}
                    />
                </div>
            )}
        />
    );
}
